import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';

import circuitService, { Circuito, FtthCaixa, CreateCircuitData, UpdateCircuitData } from '../../services/circuitService';
import { ftthCaixaService } from '../../services/ftthCaixaService';
import EscritorioSelect from '../selects/EscritorioSelect';

interface CircuitFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  circuito?: Circuito | null;
}

const CircuitForm: React.FC<CircuitFormProps> = ({
  open,
  onClose,
  onSuccess,
  circuito
}) => {
  const [selectedEscritorioId, setSelectedEscritorioId] = useState<number | ''>('');
  const [formData, setFormData] = useState<CreateCircuitData>({
    identificacao: '',
    caixa_id: 0,
    endereco: '',
  });
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(circuito);

  // Limpar estados ao abrir o modal
  useEffect(() => {
    if (open) {
      // Limpar estados quando abrir o modal
      setSelectedEscritorioId('');
      setFormData({
        identificacao: '',
        caixa_id: 0,
        endereco: '',
      });
      setCaixas([]);
      setError(null);

      // Se for edição, carregar dados do circuito
      if (circuito) {
        setFormData({
          identificacao: circuito.identificacao,
          caixa_id: circuito.caixa_id,
          endereco: circuito.endereco,
        });
        // TODO: Carregar cidade da caixa do circuito (se implementado no backend)
        // Por enquanto, deixar vazio
        setSelectedEscritorioId('');
      }
    }
  }, [open, circuito]);

  // Carregar CAIXAs quando escritório for selecionado
  useEffect(() => {
    if (selectedEscritorioId) {
      // Limpar caixas anteriores e seleção de caixa ao trocar escritório
      setCaixas([]);
      setFormData(prev => ({
        ...prev,
        caixa_id: 0
      }));
      loadCaixas(selectedEscritorioId as number);
    } else {
      setCaixas([]);
      setFormData(prev => ({
        ...prev,
        caixa_id: 0
      }));
    }
  }, [selectedEscritorioId]);

  const loadCaixas = async (escritorioId: number) => {
    try {
      setLoadingCaixas(true);
      setError(null);

      console.log('🔍 Carregando CAIXAs do escritório:', escritorioId);

      const response = await ftthCaixaService.getCaixas({
        limit: 100,
        escritorio_id: escritorioId
      });

      let caixasData: FtthCaixa[] = [];

      // Verificar estrutura da resposta
      if (response && response.success && response.data && response.data.caixas) {
        caixasData = response.data.caixas;
      } else if (response && response.caixas) {
        caixasData = response.caixas;
      } else if (response && Array.isArray(response.data)) {
        caixasData = response.data;
      } else if (Array.isArray(response)) {
        caixasData = response;
      } else {
        console.error('❌ Estrutura de resposta inesperada da API CAIXAs');
        setError('Formato de dados inesperado da API');
        return;
      }

      console.log('✅ CAIXAs carregadas:', caixasData.length);

      if (caixasData.length === 0) {
        setCaixas([]); // Garantir que o array de caixas está vazio
        //setError('Nenhuma CAIXA encontrada neste escritório');
        return;
      }

      setCaixas(caixasData);
      setError(null); // Limpar erro quando caixas são carregadas com sucesso
    } catch (error: any) {
      console.error('❌ Erro ao carregar CAIXAs:', error);
      setCaixas([]); // Limpar caixas em caso de erro
      setError(`Erro ao carregar CAIXAs: ${error.message}`);
    } finally {
      setLoadingCaixas(false);
    }
  };

  const handleChange = (field: keyof CreateCircuitData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> |
    { target: { value: any } }
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'identificacao' || field === 'caixa_id' ? Number(value) : value
    }));
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    // Como estamos usando seleção única (multiple=false), esperamos apenas um valor
    if (Array.isArray(value)) {
      // Se por algum motivo retornar array, pegar o primeiro valor
      const firstValue = value[0];
      setSelectedEscritorioId(firstValue || '');
    } else if (value !== null) {
      // Valor único (number)
      setSelectedEscritorioId(value);
    } else {
      // Valor null
      setSelectedEscritorioId('');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Validações
    if (!selectedEscritorioId) {
      setError('Escritório é obrigatório');
      return;
    }

    if (!formData.identificacao) {
      setError('Identificação é obrigatória');
      return;
    }

    if (!formData.caixa_id) {
      setError('CAIXA é obrigatória');
      return;
    }

    if (!formData.endereco.trim()) {
      setError('Endereço é obrigatório');
      return;
    }

    try {
      setLoading(true);

      if (isEdit && circuito) {
        const updateData: UpdateCircuitData = { ...formData };
        await circuitService.updateCircuit(circuito.id, updateData);
      } else {
        await circuitService.createCircuit(formData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar circuito:', error);
      const errorMessage = error.response?.data?.message ||
        `Erro ao ${isEdit ? 'atualizar' : 'criar'} circuito`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedEscritorioId('');
      setFormData({
        identificacao: '',
        caixa_id: 0,
        endereco: '',
      });
      setCaixas([]);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        {isEdit ? 'Editar Circuito' : 'Novo Circuito'}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Cidade */}
            <EscritorioSelect
              value={selectedEscritorioId === '' ? null : selectedEscritorioId}
              onChange={handleEscritorioChange}
              fullWidth={true}
              size="small"
              required={true}
              label="Escritório"
              placeholder="Selecione um escritório"
              multiple={false}
              onlyActive={true}
            />

            {/* Identificação */}
            <TextField
              label="Identificação"
              type="number"
              value={formData.identificacao || ''}
              onChange={handleChange('identificacao')}
              size="small"
              fullWidth
              required
              disabled={loading}
              inputProps={{ min: 1 }}
            />

            {/* CAIXA */}
            <FormControl fullWidth required disabled={!selectedEscritorioId || loading || loadingCaixas}>
              <InputLabel>CAIXA</InputLabel>
              <Select
                value={formData.caixa_id || ''}
                onChange={handleChange('caixa_id')}
                label="CAIXA"
                size="small"
              >
                {loadingCaixas ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    <Typography sx={{ ml: 1 }}>Carregando caixas...</Typography>
                  </MenuItem>
                ) : caixas.length === 0 && selectedEscritorioId ? (
                  <MenuItem disabled>
                    <Typography>Nenhuma caixa encontrada neste escritório</Typography>
                  </MenuItem>
                ) : (
                  caixas.map((caixa) => (
                    <MenuItem key={caixa.id} value={caixa.id}>
                      {caixa.name} - {caixa.address}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Endereço */}
            <TextField
              label="Endereço"
              value={formData.endereco}
              onChange={handleChange('endereco')}
              size="small"
              fullWidth
              required
              disabled={loading}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || loadingCaixas}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CircuitForm; 